// ═══════════════════════════════════════════════════════════════════════════
// PAGE: PROJECT — risk table rendering (lot B.10)
// Migration S5.1 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// renderProjectPage + rendu table risques (build/trend/sort/dragDrop) +
// helpers (trigramHtml, normalizeIds/Action, openUserPickerModal,
// buildActionRow/SubRow, computeStats).
//
// Bare refs : ps (state), userLabel (core.js), saveUpdate/showAddRiskModal/
// onRiskTableClick/Change/Input/showRiskHistory (autres sous-fichiers).

import { navigateTo } from '../../router.js';
import {
  isOffStage as isOffStagePure,
  isRiskVisible as isRiskVisiblePure,
  type RevisionViewState,
} from '../../domain/revision-risks.js';

// window.componentActionRow est typé dans component-action-row.ts.

// ── Population de l'écran : quels risques appartiennent à cette révision ────
//
// La règle vit dans `domain/revision-risks.ts` — elle y est documentée et
// testée. Ici on ne fait que lui présenter l'état de la page : c'est la seule
// glu qui reste, et elle n'a pas de décision à prendre.

function viewState(): RevisionViewState {
  return {
    closed: !!ps.revision?.is_closed,
    currentStage: ps.selectedStage || null,
    entries: ps.entries || {},
    ...(ps.visibilityFilter !== undefined ? { visibilityFilter: ps.visibilityFilter } : {}),
    ...(ps.showEscalatedOnly !== undefined ? { showEscalatedOnly: ps.showEscalatedOnly } : {}),
    ...(ps.showNonRelevant !== undefined ? { showNonRelevant: ps.showNonRelevant } : {}),
  };
}

function isOffStage(r: PageRisk): boolean {
  return isOffStagePure(r, viewState());
}

function isRiskVisible(r: PageRisk): boolean {
  return isRiskVisiblePure(r, viewState());
}

function renderProjectPage(): void {
  const p = ps.project;
  const rev = ps.revision;
  if (!p) return;
  const isClosed = !!(rev && rev.is_closed);
  const revDates = rev
    ? ` <span style="font-size:12px;color:var(--text3);font-weight:400">${fmtDate(rev.date_start)} → ${rev.date_end ? fmtDate(rev.date_end) : esc(t('pageProject.rev.dateInProgress'))}</span>`
    : '';
  const revBreadcrumb = rev
    ? `<a href="#project/${p.id}" style="color:var(--accent);text-decoration:underline">${esc(p.name)}</a> / ${esc(rev.name)}${revDates}${isClosed ? ` <span style="font-size:11px;background:var(--bg3);color:var(--text3);padding:1px 7px;border-radius:8px;margin-left:6px">${esc(t('pageProject.rev.closed'))}</span>` : ''}`
    : `<a href="#dashboard" style="color:var(--accent);text-decoration:underline">${esc(t('pageProject.breadcrumb.projects'))}</a> / ${esc(p.name)}`;
  // Le CTA « Challenger IA ». Gardé par un droit DÉDIÉ : l'écran s'ouvre avec
  // `risk_lists.read`, un droit de LECTURE, et un droit de lecture ne doit pas
  // suffire à dépenser des crédits ni à faire sortir des données du tenant.
  // Actif aussi sur une révision clôturée (décision 2026-08-27) : challenger a
  // posteriori une revue arrêtée est le cas d'usage d'une préparation de comité.
  const challengeBtn = window.hasPermission?.('revisions.challenge')
    ? `<button class="btn btn-secondary" id="btn-challenge-ia" style="margin-right:8px;display:inline-flex;align-items:center;gap:6px">${esc(t('challenge.cta'))}${iaCreditBadge()}</button>`
    : '';
  const matrixBtn = `<a href="#matrix/${p.id}" id="btn-matrix-from-rev" class="btn btn-secondary" style="margin-right:8px;text-decoration:none" title="${esc(t('pageProject.btn.matrixTitle', { plural: t('vocabulary.risk_plural') }))}">${esc(t('pageProject.btn.matrix'))}</a>`;
  setHeader(
    revBreadcrumb,
    isClosed
      ? `${challengeBtn}${matrixBtn}<button class="btn btn-secondary" id="btn-export-xlsx" style="margin-right:8px;display:inline-flex;align-items:center"><svg aria-hidden="true" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:6px;flex-shrink:0"><path d="M10 4v9"/><path d="M6 9l4 4 4-4"/><path d="M4 17h12"/></svg>${esc(t('pageProject.btn.exportXlsx'))}</button>`
      : `${challengeBtn}${matrixBtn}<button class="btn btn-secondary" id="btn-export-xlsx" style="margin-right:8px;display:inline-flex;align-items:center"><svg aria-hidden="true" width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:6px;flex-shrink:0"><path d="M10 4v9"/><path d="M6 9l4 4 4-4"/><path d="M4 17h12"/></svg>${esc(t('pageProject.btn.exportXlsx'))}</button>
         <button class="btn btn-primary" id="btn-save-update" style="margin-right:8px">${esc(t('pageProject.btn.save'))}</button>
         <button class="btn btn-secondary" id="btn-close-revision">${esc(t('pageProject.btn.closeRevision'))}</button>`,
  );

  const statsHtml = '';

  const controlsHtml = `
    <div class="controls-bar" id="controls-bar">
      ${ps.selectedStage ? `<span style="font-size:12px;color:var(--text2)">${esc(t('pageProject.controls.stage'))} <strong>${esc(ps.selectedStage)}</strong></span>` : ''}
      <label style="margin-left:8px">${esc(t('pageProject.controls.visibility'))}</label>
      <select id="visibility-filter" style="font-size:12px;padding:4px 8px">
        <option value="all">${esc(t('pageProject.visibility.all'))}</option>
        <option value="publique">${esc(t('pageProject.visibility.public'))}</option>
        <option value="privée">${esc(t('pageProject.visibility.private'))}</option>
      </select>
      <label style="margin-left:8px">
        <input type="checkbox" id="chk-show-nonrelevant" style="margin-right:4px" checked>
        ${esc(t('pageProject.controls.showNonRel'))}
      </label>
      <label style="margin-left:8px">
        <input type="checkbox" id="chk-escalated-only" style="margin-right:4px" ${ps.showEscalatedOnly ? 'checked' : ''}>
        ${esc(t('pageProject.controls.escalatedOnly'))}
      </label>
      ${!isClosed ? `<button class="btn btn-secondary btn-sm ml-auto" id="btn-add-risk">${esc(t('pageProject.btn.addRisk', { object: t('vocabulary.risk') }))}</button>` : ''}
    </div>`;

  const tableHtml = buildRiskTable();

  setContent(statsHtml + controlsHtml + tableHtml);

  const visFilter = document.getElementById('visibility-filter') as HTMLSelectElement | null;
  visFilter?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLSelectElement;
    ps.visibilityFilter = target.value;
    reRenderTable();
    updateStats();
  });

  const showNonRelChk = document.getElementById('chk-show-nonrelevant') as HTMLInputElement | null;
  showNonRelChk?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLInputElement;
    ps.showNonRelevant = target.checked;
    reRenderTable();
    updateStats();
  });

  const escalatedOnlyChk = document.getElementById('chk-escalated-only') as HTMLInputElement | null;
  escalatedOnlyChk?.addEventListener('change', (e: Event) => {
    const target = e.target as HTMLInputElement;
    ps.showEscalatedOnly = target.checked;
    reRenderTable();
    updateStats();
  });

  document.getElementById('btn-add-risk')?.addEventListener('click', () => {
    void showAddRiskModal();
  });

  const tableEl = document.getElementById('risk-table-wrap');
  if (tableEl) {
    tableEl.addEventListener('click', onRiskTableClick);
    tableEl.addEventListener('change', onRiskTableChange);
    tableEl.addEventListener('input', onRiskTableInput);
  }

  document
    .getElementById('btn-export-xlsx')
    ?.addEventListener('click', () => window.showExportRevisionModal(rev as never, p as never));
  document.getElementById('btn-challenge-ia')?.addEventListener('click', () => {
    if (rev) window.openChallengeModal(rev.id);
  });
  document.getElementById('btn-save-update')?.addEventListener('click', () => {
    void saveUpdate();
  });
  // CTA Matrice : si des modifications de la révision ne sont pas enregistrées,
  // on propose de les enregistrer avant d'ouvrir la matrice ; sinon on reste
  // sur la révision. On pose aussi un backHash pour le fil d'ariane de la
  // matrice (retour vers la révision).
  document.getElementById('btn-matrix-from-rev')?.addEventListener('click', (e: Event) => {
    e.preventDefault();
    const _revDirty = (): boolean =>
      !!(
        ps.dirtyRisks?.size ||
        ps.dirtyActions?.size ||
        ps.newActions?.length ||
        ps.deletedActionIds?.length ||
        ps.newLinks?.length ||
        ps.deletedLinkIds?.length
      );
    const go = (): void => {
      try {
        sessionStorage.setItem('matrix:backHash', window.location.hash);
      } catch {
        /* sessionStorage indisponible — pas de retour révision au fil d'ariane */
      }
      window.location.hash = '#matrix/' + p.id;
    };
    if (!_revDirty()) {
      go();
      return;
    }
    openModal(
      t('pageProject.matrixUnsaved.title'),
      t('pageProject.matrixUnsaved.body'),
      async () => {
        closeModal();
        await saveUpdate();
        // Si la sauvegarde a réussi, l'état dirty est purgé → on peut ouvrir la
        // matrice ; sinon on reste sur la révision.
        if (!_revDirty()) go();
      },
      t('pageProject.matrixUnsaved.btn'),
    );
  });
  document.getElementById('btn-close-revision')?.addEventListener('click', () => {
    openModal(
      t('pageProject.closeRev.title'),
      t('pageProject.closeRev.bodyOpen'),
      async () => {
        try {
          if (!ps.revision) return;
          await api.closeRevision(ps.revision.id);
          closeModal();
          _removeUnloadGuard();
          toast(t('pageProject.closeRev.toast'));
          navigateTo('project/:id', { id: ps.project?.id ?? 0 });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : t('common.error');
          toast(msg, 'error');
        }
      },
      t('pageProject.closeRev.btn'),
    );
  });
}

function computeStats(): {
  active: number;
  maxSev: number;
  critCount: number;
  problemCount: number;
  actionCount: number;
} {
  const risks = ps.risks || [];
  const entries = ps.entries || {};
  const active = risks.filter((r) => isRiskVisible(r));
  const sevs = active.map((r) => {
    const e = entries[r.id] || ({} as { probability?: number; impact?: number });
    return (e.probability || 1) * (e.impact || 1);
  });
  return {
    active: active.length,
    maxSev: sevs.length ? Math.max(...sevs) : 0,
    critCount: sevs.filter((s) => s >= 9).length,
    problemCount: 0,
    actionCount: risks.reduce((n, r) => {
      const e = entries[r.id];
      return n + ((e?.actions || []).length + (e?.linked_actions || []).length);
    }, 0),
  };
}

function updateStats(): void {
  const stats = computeStats();
  const statCards = document.querySelectorAll<HTMLElement>('.stat-card .stat-value');
  if (statCards.length < 5) return;
  const [c0, c1, c2, c3, c4] = statCards;
  if (!c0 || !c1 || !c2 || !c3 || !c4) return;
  c0.textContent = String(stats.active);
  c1.textContent = String(stats.maxSev);
  c1.className =
    'stat-value' + (stats.maxSev >= 12 ? ' stat-danger' : stats.maxSev >= 8 ? ' stat-warn' : '');
  c2.textContent = String(stats.critCount);
  c2.className = 'stat-value' + (stats.critCount > 0 ? ' stat-danger' : '');
  c3.textContent = String(stats.problemCount);
  c3.className = 'stat-value' + (stats.problemCount > 0 ? ' stat-danger' : '');
  c4.textContent = String(stats.actionCount);
}

function buildRiskTable(): string {
  const sorted = sortedRisks();
  const innerHtml = sorted.length
    ? `<div class="table-wrap">
        <table id="risk-table">
          <thead><tr>${buildRiskTableHeader()}</tr></thead>
          <tbody id="risk-tbody">${sorted.map((r) => buildRiskRow(r)).join('')}</tbody>
        </table>
      </div>`
    : `<div class="empty" style="padding:24px;text-align:center;color:var(--text3)">
        ${(ps.risks || []).length ? esc(t('pageProject.empty.filtered')) : t('pageProject.empty.noRisks')}
       </div>`;
  return `<div id="risk-table-wrap">${innerHtml}</div>`;
}

function buildRiskTableHeader(): string {
  const cols: Array<{ key: string | null; label: string; checkbox?: boolean }> = [
    { key: null, label: t('pageProject.col.relevant'), checkbox: true },
    { key: null, label: t('pageProject.col.code') },
    { key: 'category', label: t('pageProject.col.cat') },
    { key: null, label: t('pageProject.col.cause') },
    { key: null, label: t('pageProject.col.event') },
    { key: 'probability', label: t('pageProject.col.prob') },
    { key: 'impact', label: t('pageProject.col.impact') },
    { key: 'severity', label: t('pageProject.col.severity') },
    { key: null, label: t('pageProject.col.trend') },
    { key: 'owner', label: t('pageProject.col.owner') },
    { key: null, label: '' },
  ];
  return cols
    .map((c) => {
      if (c.key) {
        const isSorted = ps.sortBy === c.key;
        const arrow = isSorted ? (ps.sortDir === 'asc' ? '↑' : '↓') : '↕';
        return `<th class="sortable${isSorted ? ' sorted' : ''}" data-sort="${c.key}">${esc(c.label)} <span class="sort-arrow">${arrow}</span></th>`;
      }
      return `<th>${esc(c.label)}</th>`;
    })
    .join('');
}

function trendHtml(riskId: number, currentSev: number | null, isRelevant: boolean): string {
  if (!isRelevant) return '<span style="color:var(--text3);font-size:11px">—</span>';
  const prevE = ps.prevEntries ? ps.prevEntries[String(riskId)] : null;

  const prevSev =
    prevE && prevE.is_relevant !== false && prevE.probability && prevE.impact
      ? prevE.probability * prevE.impact
      : null;

  if (currentSev != null && currentSev >= 4 && (prevSev === null || prevSev < 4)) {
    return `<span style="background:#facc15;color:#713f12;font-size:10px;font-weight:700;padding:2px 6px;border-radius:10px">${esc(t('pageProject.trend.new'))}</span>`;
  }
  if (prevSev === null)
    return `<span style="color:var(--text3);font-size:11px" title="${esc(t('pageProject.trend.firstTitle'))}">·</span>`;
  if (currentSev !== null && currentSev > prevSev)
    return `<span style="color:var(--danger);font-size:16px" title="${esc(t('pageProject.trend.upTitle'))}">↑</span>`;
  if (currentSev !== null && currentSev < prevSev)
    return `<span style="color:var(--ok);font-size:16px" title="${esc(t('pageProject.trend.downTitle'))}">↓</span>`;
  return `<span style="color:var(--text);font-size:16px" title="${esc(t('pageProject.trend.stableTitle'))}">→</span>`;
}

function buildRiskRow(r: PageRisk): string {
  const entries = ps.entries || {};
  const fallbackEntry = {
    probability: 1,
    impact: 1,
    is_relevant: true,
    owner_id: null as number | null,
    actions: [] as PageAction[],
    linked_actions: [] as PageAction[],
  };
  const e = entries[r.id] || fallbackEntry;
  const isRelevant = e.is_relevant !== false;
  const prob = isRelevant ? (e.probability ?? 1) : null;
  const impact = isRelevant ? (e.impact ?? 1) : null;
  const sev = prob && impact ? prob * impact : null;
  const col = sev ? sevColor(sev) : '';
  const active = !isOffStage(r);
  const stagesJson = esc(JSON.stringify(r.stages || []));
  const expanded = ps.expandedRiskId === r.id;

  // Affichage sévérité : badge avec valeur, fond coloré (palette admin),
  // texte noir/blanc selon luminance (RefactoUX/Actions mai 2026, homogène
  // avec la zone "Utilisée dans risques" de l'éditeur d'action). Le libellé
  // textuel (Critique/Élevé/…) reste en dessous pour le contexte verbal.
  const _textOnHex = (hex: string): string => {
    const m = /^#([0-9a-f]{6})$/i.exec(hex);
    if (!m) return '#1a1a1a';
    const code = m[1] as string;
    const r2 = parseInt(code.substring(0, 2), 16);
    const g2 = parseInt(code.substring(2, 4), 16);
    const b2 = parseInt(code.substring(4, 6), 16);
    const lum = (0.299 * r2 + 0.587 * g2 + 0.114 * b2) / 255;
    return lum > 0.6 ? '#1a1a1a' : '#ffffff';
  };
  const sevDisplay =
    sev !== null
      ? `<div class="sev" id="sev-${r.id}">
          <span class="sev-value" style="display:inline-block;min-width:36px;padding:3px 10px;text-align:center;background:${col};color:${_textOnHex(col)};border-radius:6px;font-weight:700;line-height:1.2">${sev}</span>
          <span class="sev-label" style="color:${col}">${sevLabel(sev)}</span>
        </div>`
      : `<div class="sev" id="sev-${r.id}"><span class="sev-value" style="font-size:13px;color:var(--text3)">${esc(t('pageProject.row.na'))}</span></div>`;

  const isClosed = !!(ps.revision && ps.revision.is_closed);
  const probCell = isRelevant
    ? isClosed
      ? `<span style="font-family:var(--font-mono);font-size:14px;font-weight:600">${prob}</span>`
      : scaleHtml(r.id, 'prob', prob ?? 1)
    : '<span style="color:var(--text3);font-size:12px">—</span>';
  const impCell = isRelevant
    ? isClosed
      ? `<span style="font-family:var(--font-mono);font-size:14px;font-weight:600">${impact}</span>`
      : scaleHtml(r.id, 'impact', impact ?? 1)
    : '<span style="color:var(--text3);font-size:12px">—</span>';
  const users = ps.users || [];
  const ownerSel = isClosed
    ? `<span style="font-size:12px">${esc(users.find((u) => u.id === e.owner_id)?.name || '—')}</span>`
    : `<select class="entry-owner" data-owner-risk="${r.id}" style="min-width:100px;font-size:12px">${individualUserOptions(users, e.owner_id)}</select>`;
  const totalActions = (e.actions || []).length + (e.linked_actions || []).length;
  // RefactoUX mai 2026 : le compteur d'actions est sorti du bouton triangle
  // pour être plus visible à sa droite. Le bouton garde uniquement le
  // chevron — moins encombré, le compteur respire.
  const countBadge =
    totalActions > 0
      ? `<span style="background:var(--accent-bg);color:var(--accent);font-size:11px;font-weight:600;padding:1px 7px;border-radius:8px;margin-left:6px;display:inline-block;min-width:20px;text-align:center">${totalActions}</span>`
      : '';
  const expandBtn = `<button class="btn btn-secondary btn-icon" data-expand-risk="${r.id}" title="${esc(t(expanded ? 'pageProject.row.collapseTitle' : 'pageProject.row.expandTitle'))}">${expanded ? '▼' : '▶'}</button>${countBadge}`;
  const histBtn = `<button class="btn btn-secondary btn-icon" data-history-risk="${r.id}" title="${esc(t('pageProject.row.historyTitle'))}" style="margin-left:2px">◷</button>`;
  // Révision clôturée → lecture seule : pas de modification (crayon) ni de
  // suppression de risque.
  const editBtn = isClosed
    ? ''
    : `<button class="btn btn-secondary btn-icon" data-edit-risk="${r.id}" title="${esc(t('pageProject.row.editTitle'))}" style="margin-left:2px">✎</button>`;
  const delBtn =
    !r.source_risk_id && !isClosed
      ? `<button class="btn btn-danger btn-icon" data-del-risk="${r.id}" title="${esc(t('pageProject.row.delTitle'))}" style="margin-left:2px">✕</button>`
      : '';

  const catAbbr = (r.category || '').slice(0, 2).toUpperCase() || '?';
  const catPicto = `<span title="${esc(r.category || '—')}" style="display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;background:var(--bg3);border:1px solid var(--border);border-radius:50%;font-size:10px;font-weight:700;color:var(--text2);cursor:default">${esc(catAbbr)}</span>`;

  const expandHighlight = expanded
    ? 'background:var(--accent-bg);border-left:3px solid var(--accent);'
    : '';

  let row = `<tr class="risk-row${active ? '' : ' inactive'}${!isRelevant ? ' nonrelevant' : ''}${expanded ? ' expanded' : ''}" data-risk-id="${r.id}" data-risk-stages='${stagesJson}' style="${expandHighlight}">
    <td style="text-align:center"><input type="checkbox" class="relevant-chk" data-relevant-risk="${r.id}" ${isRelevant ? 'checked' : ''} title="${esc(t(isRelevant ? 'pageProject.row.relevantOn' : 'pageProject.row.relevantOff'))}"></td>
    <td style="width:48px"><span data-expand-risk="${r.id}" title="${esc(t(expanded ? 'pageProject.row.collapseTitle' : 'pageProject.row.expandTitle'))}" style="font-family:var(--font-mono);font-size:11px;font-weight:600;color:var(--accent);text-decoration:underline;cursor:pointer">${esc(r.code || 'R' + r.id)}</span></td>
    <td style="text-align:center">${catPicto}</td>
    <td style="max-width:160px;font-size:12px;white-space:pre-line;line-height:1.4">${esc(r.cause || '—')}</td>
    <td style="max-width:160px;font-size:12px;white-space:pre-line;line-height:1.4">${esc(r.consequence || '—')}</td>
    <td id="prob-cell-${r.id}">${probCell}</td>
    <td id="impact-cell-${r.id}">${impCell}</td>
    <td>${sevDisplay}</td>
    <td style="text-align:center" id="trend-${r.id}">${trendHtml(r.id, sev, isRelevant)}</td>
    <td>${ownerSel}</td>
    <td style="white-space:nowrap">${expandBtn}${histBtn}${editBtn}${delBtn}</td>
  </tr>`;

  if (expanded) row += buildActionsSubRow(r.id, e.actions || [], isClosed);
  return row;
}

function normalizeIds(val: unknown): number[] {
  if (!val) return [];
  if (Array.isArray(val)) return val.map((v) => parseInt(String(v))).filter((v) => !isNaN(v));
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed)
        ? parsed.map((v: unknown) => parseInt(String(v))).filter((v) => !isNaN(v))
        : [];
    } catch {
      return [];
    }
  }
  return [];
}

function normalizeAction(a: PageAction): PageAction {
  return Object.assign({}, a, {
    performers: normalizeIds(a.performers),
    consulted: normalizeIds(a.consulted),
    informed: normalizeIds(a.informed),
    approver_id: a.approver_id ? parseInt(String(a.approver_id)) : null,
    priority: typeof a.priority === 'number' ? a.priority : 2,
    status_id: a.status_id ? parseInt(String(a.status_id)) : null,
    progress: typeof a.progress === 'number' ? a.progress : 0,
    deadline: a.deadline || null,
    deadline_revised: a.deadline_revised || null,
  });
}

function trigramHtml(
  u: { trigram?: string | null; name?: string; is_team?: boolean } | null | undefined,
  opts?: {
    extraStyle?: string;
    fallbackToName?: boolean;
    placeholder?: string;
    extraClass?: string;
  },
): string {
  const o = opts || {};
  const placeholder = o.placeholder !== undefined ? o.placeholder : '—';
  const fallbackToName = !!o.fallbackToName;
  let tri = u && u.trigram ? String(u.trigram).toUpperCase() : null;
  if (!tri && fallbackToName && u && u.name) {
    tri = u.name.slice(0, 3).toUpperCase();
  }
  if (!tri) {
    if (placeholder === '') return '';
    return `<span style="color:var(--text3)">${esc(placeholder)}</span>`;
  }
  const teamAttr = u && u.is_team ? ' data-team="true"' : '';
  const cls = o.extraClass ? `user-trig ${o.extraClass}` : 'user-trig';
  const style = o.extraStyle ? ` style="${o.extraStyle}"` : '';
  const fullName =
    u && u.name ? (u.is_team ? t('pageProject.user.team', { name: u.name }) : u.name) : '';
  const titleAttr = fullName ? ` title="${esc(fullName)}"` : '';
  return `<span class="${cls}"${teamAttr}${style}${titleAttr}>${esc(tri)}</span>`;
}

function renderTagsHtml(ids: unknown, field: string, riskId: number, idx: number): string {
  const safeIds = normalizeIds(ids);
  if (!safeIds.length) return '<span style="color:var(--text3);font-size:11px">—</span>';
  const users = ps.users || [];
  return safeIds
    .map((id) => {
      const u = users.find((uu) => uu.id === id);
      if (!u) return '';
      return `<span class="tag-chip" title="${esc(u.name)}"><span class="tag-label">${esc(userLabel(u, true))}</span><button class="tag-remove" data-remove-tag="${id}" data-field="${field}" data-action-risk="${riskId}" data-action-idx="${idx}">×</button></span>`;
    })
    .join('');
}

function refreshTagWrap(
  wrap: HTMLElement,
  field: string,
  riskId: number,
  idx: number,
  ids: unknown,
): void {
  const display = wrap.querySelector('.tags-display');
  if (display) display.innerHTML = renderTagsHtml(normalizeIds(ids), field, riskId, idx);
}

function openUserPickerModal(field: string, riskId: number, idx: number): void {
  const entries = ps.entries || {};
  const action = entries[riskId]?.actions?.[idx] as PageAction | undefined;
  if (!action) return;
  const currentIds = normalizeIds((action as Record<string, unknown>)[field]);
  const fieldLabel =
    field === 'performers'
      ? t('pageProject.field.actors')
      : field === 'consulted'
        ? t('pageProject.field.consulted')
        : t('pageProject.field.informed');

  const users = ps.users || [];
  const rows = users
    .map((u) => {
      const checked = currentIds.includes(u.id) ? 'checked' : '';
      const teamBadge = u.is_team
        ? `<span style="background:var(--accent-bg);color:var(--accent);font-size:10px;padding:1px 5px;border-radius:8px;margin-left:4px">${esc(t('pageProject.userPicker.team'))}</span>`
        : '';
      const tri = trigramHtml(u, {
        extraStyle: 'font-size:11px;min-width:32px;display:inline-block',
      });
      return `<label style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:0.5px solid var(--border);cursor:pointer">
      <input type="checkbox" class="user-pick-chk" value="${u.id}" ${checked} style="margin:0">
      ${tri}
      <span style="font-size:13px">${esc(u.name)}</span>
      ${teamBadge}
    </label>`;
    })
    .join('');

  const body = `<div style="max-height:360px;overflow-y:auto">${rows || `<p style="color:var(--text3)">${esc(t('pageProject.userPicker.empty'))}</p>`}</div>`;

  openModal(
    t('pageProject.userPicker.title', { field: fieldLabel }),
    body,
    () => {
      const selected = Array.from(
        document.querySelectorAll<HTMLInputElement>('.user-pick-chk:checked'),
      ).map((c) => parseInt(c.value));
      (action as Record<string, unknown>)[field] = selected;
      const wrap = document.querySelector<HTMLElement>(
        `.tag-select-wrap[data-field="${field}"][data-action-risk="${riskId}"][data-action-idx="${idx}"]`,
      );
      if (wrap) refreshTagWrap(wrap, field, riskId, idx, selected);
      closeModal();
    },
    t('pageProject.userPicker.btn'),
  );
}

function buildActionRow(
  a: PageAction,
  opts: { riskId: number; idx: number; readOnly?: boolean; isRevisionContext?: boolean },
): string {
  const { riskId, idx, readOnly = false } = opts;
  const users = ps.users || [];
  const statuses = ps.actionStatuses || [];
  const userMap: Record<number, PageUser> = Object.fromEntries(users.map((u) => [u.id, u]));
  const allProjects = ps.allProjects || [];
  const projectMap: Record<number, PageProjectMin> = Object.fromEntries(
    allProjects.map((p) => [p.id, p]),
  );
  const cuid = typeof currentUserId === 'function' ? currentUserId() : null;
  const cu = cuid ? userMap[cuid] : null;
  const userTeamIds = cu && !cu.is_team ? cu.team_ids || [] : [];
  return window.componentActionRow.buildActionRowHtml(
    a as never,
    {
      statuses,
      userMap,
      projectMap,
      readOnly,
      riskId,
      idx,
      currentUserId: cuid,
      userTeamIds,
    } as never,
  );
}

function buildActionsSubRow(riskId: number, actions: PageAction[], readOnly = false): string {
  const entries = ps.entries || {};
  const entryRaw = entries[riskId] as { linked_actions?: PageAction[] } | undefined;
  const linkedActions = entryRaw?.linked_actions || [];
  const escalatedForRow = !!(ps.risks || []).find((rr) => rr.id === riskId)?.escalated_to_program;

  let actionRows = '';
  for (let i = 0; i < actions.length; i++) {
    const action = actions[i];
    if (!action) continue;
    actionRows += buildActionRow(action, { riskId, idx: i, readOnly, isRevisionContext: true });
  }

  let linkedRows = '';
  const ls_users = ps.users || [];
  const ls_userMap: Record<number, PageUser> = Object.fromEntries(ls_users.map((u) => [u.id, u]));
  const ls_projectMap: Record<number, PageProjectMin> = Object.fromEntries(
    (ps.allProjects || []).map((p) => [p.id, p]),
  );
  for (const a of linkedActions) {
    const lStatus = (ps.actionStatuses || []).find((s) => s.id === a.status_id);
    const stStyle = lStatus
      ? `${lStatus.color ? 'color:' + lStatus.color + ';' : ''}${lStatus.bold ? 'font-weight:700;' : ''}${lStatus.italic ? 'font-style:italic;' : ''}${lStatus.strikethrough ? 'text-decoration:line-through;' : ''}`
      : '';
    const isFinal = !!(lStatus && lStatus.is_final);
    const rowStyle = isFinal ? stStyle : '';
    const lProg = typeof a.progress === 'number' ? a.progress : 0;
    const fillColor = (lStatus && lStatus.color) || 'var(--accent)';
    const approverUser = a.approver_id ? ls_userMap[a.approver_id] : null;
    const approverLabel = a.approver_id
      ? approverUser
        ? trigramHtml(approverUser, { fallbackToName: true })
        : a.approver_name || '?'
      : a.approver_name || '—';
    const performerIds = (a.performers as number[] | undefined) || [];
    const lPerformers =
      performerIds
        .map((id) => {
          const u = ls_userMap[id];
          return u ? trigramHtml(u, { fallbackToName: true }) : '?';
        })
        .join(', ') || '—';
    const consultedIds = (a.consulted as number[] | undefined) || [];
    const lConsulted =
      consultedIds
        .map((id) => {
          const u = ls_userMap[id];
          return u ? trigramHtml(u, { fallbackToName: true }) : '?';
        })
        .join(', ') || '—';
    let datesCell;
    if (!a.deadline && !a.deadline_revised) {
      datesCell = `<span style="color:var(--danger);cursor:help;font-size:14px" title="${esc(t('actionRow.warn.noDate'))}">⚠</span>`;
    } else if (a.deadline && a.deadline_revised && a.deadline !== a.deadline_revised) {
      datesCell = `<div>${esc(fmtDate(a.deadline))}</div><div style="font-size:11px">→ <strong>${esc(fmtDate(a.deadline_revised))}</strong></div>`;
    } else {
      datesCell = `<div>${esc(fmtDate(a.deadline || a.deadline_revised))}</div>`;
    }
    linkedRows += `
      <tr class="action-row linked-row" style="background:var(--accent-bg);${rowStyle}">
        <td style="width:48px;text-align:center;font-size:var(--fs-xs)">
          <span style="font-family:var(--font-mono);font-size:11px;font-weight:600;color:var(--accent)">${a.id != null ? '#' + a.id : ''}</span>
        </td>
        <td style="font-size:12px;max-width:240px">
          <span style="font-style:italic">${esc(a.text || '—')}</span>
          <span style="font-size:10px;background:var(--accent);color:#fff;padding:1px 5px;border-radius:8px;margin-left:6px">${esc(t('pageProject.linked.badge'))}</span>
        </td>
        <td style="min-width:130px">
          <div style="${stStyle};font-size:12px">${lStatus ? esc(lStatus.name || '') : '—'}</div>
          <div style="display:flex;align-items:center;gap:4px;margin-top:3px">
            <div style="width:60px;height:5px;background:var(--bg3);border-radius:4px;overflow:hidden">
              <div style="height:100%;width:${lProg}%;background:${fillColor}"></div>
            </div>
            <span style="font-size:11px">${lProg}%</span>
          </div>
        </td>
        <td style="font-size:11px;white-space:nowrap">${esc(priorityLabel(a.priority))}</td>
        <td style="font-size:12px;white-space:nowrap">${datesCell}</td>
        <td style="font-size:11px;min-width:170px">
          <div><span style="color:var(--text3);font-weight:600">(R)</span> : ${lPerformers}</div>
          <div><span style="color:var(--text3);font-weight:600">(A)</span> : ${approverLabel}</div>
          <div><span style="color:var(--text3);font-weight:600">(C)</span> : ${lConsulted}</div>
        </td>
        <td style="font-size:var(--fs-xs);white-space:nowrap">${
          a.project_ids && a.project_ids.length
            ? `<span title="${esc(
                (a.project_ids || [])
                  .map((id) => {
                    const p = ls_projectMap[id];
                    return p ? `[${p.code || '?'}] ${p.name || ''}` : '';
                  })
                  .filter(Boolean)
                  .join('\n'),
              )}">${(a.project_ids || [])
                .map((id) => {
                  const p = ls_projectMap[id];
                  return esc(p?.code || '?');
                })
                .join(', ')}</span>`
            : `<span style="color:var(--warn);cursor:help;font-size:14px" title="${esc(t('actionRow.warn.noProject'))}">⚠</span>`
        }</td>
        <td style="text-align:center">${
          a.origins && a.origins.length
            ? a.origins
                .map((o) => {
                  const codeProj = o.project_code || '?';
                  const codeRisk = o.risk_code || (o.risk_id ? 'R' + o.risk_id : '?');
                  const tip =
                    o.project_name || o.risk_category
                      ? `${o.project_name || ''} - ${o.risk_category || ''}`
                      : '';
                  return `<div title="${esc(tip)}">${esc(codeProj)} - ${esc(codeRisk)}</div>`;
                })
                .join('')
            : '<span style="color:var(--text3)">—</span>'
        }</td>
        <td style="font-size:var(--fs-xs);white-space:nowrap">
          <div>${a.updated_at ? esc(fmtDateLocal(a.updated_at)) : '—'}</div>
        </td>
        <td style="white-space:nowrap">
          ${
            readOnly
              ? ''
              : `<button class="btn btn-secondary btn-icon" data-edit-linked-action="${a.id}" data-edit-linked-risk="${riskId}" title="${esc(t('pageProject.linked.editTitle'))}" style="font-size:11px">✎</button>
          <button class="btn btn-secondary btn-icon" data-unlink-action="${a.id}" data-unlink-risk="${riskId}" title="${esc(t('pageProject.linked.unlinkTitle'))}">✕</button>`
          }
        </td>
      </tr>`;
  }

  const totalCount = actions.length + linkedActions.length;
  return `<tr class="risk-actions-row" data-actions-for="${riskId}">
    <td colspan="11">
      <div class="risk-actions-panel">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">
          <span style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.06em;color:var(--text3)">
            ${esc(t(totalCount === 1 ? 'pageProject.actions.titleOne' : 'pageProject.actions.titleMany', { n: totalCount, singular: t('vocabulary.action'), plural: t('vocabulary.action_plural') }))}
          </span>
          <div style="display:flex;gap:6px;align-items:center">
            ${
              readOnly
                ? ''
                : `
            <label style="display:flex;align-items:center;gap:5px;font-size:12px;cursor:pointer;color:var(--text2);margin-right:4px" title="${esc(t('pageProject.escalate.tooltip'))}">
              <input type="checkbox" class="escalate-chk" data-escalate-risk="${riskId}" ${escalatedForRow ? 'checked' : ''} style="width:auto;margin:0">
              ${esc(t('pageProject.escalate.label'))}
            </label>
            <button class="btn btn-secondary btn-sm" data-link-action-risk="${riskId}">${esc(t('pageProject.actions.btnLink'))}</button>
            <button class="btn btn-secondary btn-sm" data-copy-action-risk="${riskId}">${esc(t('pageProject.actions.btnCopy'))}</button>
            <button class="btn btn-secondary btn-sm" data-add-action-risk="${riskId}">${esc(t('pageProject.actions.btnNew'))}</button>
            `
            }
          </div>
        </div>
        ${
          totalCount > 0
            ? `
        <table class="action-table">
          <thead><tr>
            <th style="width:48px">#</th>
            <th>${esc(t('pageProject.actions.colDesc'))}</th>
            <th>${esc(t('pageProject.actions.colStatus'))}</th>
            <th>${esc(t('pageProject.actions.colPriority'))}</th>
            <th>${esc(t('pageProject.actions.colDeadline'))}</th>
            <th>${esc(t('pageProject.actions.colRaci'))}</th>
            <th>${esc(t('pageProject.actions.colProjects'))}</th>
            <th>${esc(t('pageProject.actions.colRisks'))}</th>
            <th>${esc(t('pageProject.actions.colUpdated'))}</th>
            <th></th>
          </tr></thead>
          <tbody id="action-tbody-${riskId}">${actionRows}${linkedRows}</tbody>
        </table>`
            : `<p style="color:var(--text3);font-size:12px;font-style:italic">${esc(t('pageProject.actions.empty'))}</p>`
        }
      </div>
    </td>
  </tr>`;
}

function applySort(): void {
  const risks = ps.risks || [];
  const entries = ps.entries || {};
  const users = ps.users || [];
  const all = [...risks];
  all.sort((a, b) => {
    const ea =
      entries[a.id] || ({} as { probability?: number; impact?: number; owner_id?: number | null });
    const eb =
      entries[b.id] || ({} as { probability?: number; impact?: number; owner_id?: number | null });
    const aActive = isOffStage(a) ? 1 : 0;
    const bActive = isOffStage(b) ? 1 : 0;
    if (aActive !== bActive) return aActive - bActive;
    let va: string | number;
    let vb: string | number;
    switch (ps.sortBy) {
      case 'category':
        va = (a.category || '').toLowerCase();
        vb = (b.category || '').toLowerCase();
        break;
      case 'probability':
        va = ea.probability || 0;
        vb = eb.probability || 0;
        break;
      case 'impact':
        va = ea.impact || 0;
        vb = eb.impact || 0;
        break;
      case 'owner': {
        const ua = users.find((u) => u.id === ea.owner_id);
        const ub2 = users.find((u) => u.id === eb.owner_id);
        va = (ua ? ua.name : '').toLowerCase();
        vb = (ub2 ? ub2.name : '').toLowerCase();
        break;
      }
      default:
        va = (ea.probability || 0) * (ea.impact || 0);
        vb = (eb.probability || 0) * (eb.impact || 0);
        break;
    }
    if (va < vb) return ps.sortDir === 'asc' ? -1 : 1;
    if (va > vb) return ps.sortDir === 'asc' ? 1 : -1;
    return 0;
  });
  ps.stableOrder = all.map((r) => r.id);
}

function sortedRisks(): PageRisk[] {
  const risks = ps.risks || [];
  const stableOrder = ps.stableOrder || [];
  const filtered = new Set<number>(risks.filter((r) => isRiskVisible(r)).map((r) => r.id));
  const riskMap: Record<number, PageRisk> = Object.fromEntries(risks.map((r) => [r.id, r]));
  const result: PageRisk[] = stableOrder
    .filter((id) => filtered.has(id))
    .map((id) => riskMap[id])
    .filter((r): r is PageRisk => !!r);
  risks.forEach((r) => {
    if (filtered.has(r.id) && !stableOrder.includes(r.id)) result.push(r);
  });
  return result;
}

function reRenderTable(): void {
  const wrap = document.getElementById('risk-table-wrap');
  if (!wrap) return;
  wrap.outerHTML = buildRiskTable();
  const tableEl = document.getElementById('risk-table-wrap');
  if (tableEl) {
    tableEl.addEventListener('click', onRiskTableClick);
    tableEl.addEventListener('change', onRiskTableChange);
    tableEl.addEventListener('input', onRiskTableInput);
  }
  const vf = document.getElementById('visibility-filter') as HTMLSelectElement | null;
  if (vf) vf.value = ps.visibilityFilter || 'all';
  const snr = document.getElementById('chk-show-nonrelevant') as HTMLInputElement | null;
  if (snr) snr.checked = !!ps.showNonRelevant;
  if (ps.expandedRiskId) initActionDragDrop(ps.expandedRiskId);
}

function initActionDragDrop(riskId: number): void {
  const tbody = document.getElementById(`action-tbody-${riskId}`);
  if (!tbody) return;
  let dragSrc: HTMLElement | null = null;

  tbody.addEventListener('dragstart', (e: DragEvent) => {
    const target = e.target as HTMLElement | null;
    dragSrc = target?.closest<HTMLElement>('tr.action-row') ?? null;
    if (!dragSrc) return;
    dragSrc.style.opacity = '0.4';
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  });

  tbody.addEventListener('dragend', () => {
    if (dragSrc) dragSrc.style.opacity = '';
    tbody
      .querySelectorAll<HTMLElement>('tr.action-row')
      .forEach((r) => r.classList.remove('action-drag-over'));
    dragSrc = null;
  });

  tbody.addEventListener('dragover', (e: DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('tr.action-row') ?? null;
    tbody
      .querySelectorAll<HTMLElement>('tr.action-row')
      .forEach((r) => r.classList.remove('action-drag-over'));
    if (target && target !== dragSrc) target.classList.add('action-drag-over');
  });

  tbody.addEventListener('dragleave', (e: DragEvent) => {
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('tr.action-row') ?? null;
    if (target) target.classList.remove('action-drag-over');
  });

  tbody.addEventListener('drop', (e: DragEvent) => {
    e.preventDefault();
    const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('tr.action-row') ?? null;
    tbody
      .querySelectorAll<HTMLElement>('tr.action-row')
      .forEach((r) => r.classList.remove('action-drag-over'));
    if (!target || !dragSrc || target === dragSrc) return;

    const rows = Array.from(tbody.querySelectorAll<HTMLElement>('tr.action-row'));
    const srcIdx = rows.indexOf(dragSrc);
    const tgtIdx = rows.indexOf(target);
    if (srcIdx < tgtIdx) tbody.insertBefore(dragSrc, target.nextSibling);
    else tbody.insertBefore(dragSrc, target);
    dragSrc.style.opacity = '';

    const entries = ps.entries || {};
    const entry = entries[riskId];
    const actions = entry?.actions;
    if (!actions || !entry) return;
    const newOrder = Array.from(tbody.querySelectorAll<HTMLElement>('tr.action-row'))
      .map((tr) => {
        const oldIdx = parseInt(tr.dataset['actionIdx'] || '0');
        return actions[oldIdx];
      })
      .filter((a): a is PageAction => !!a);
    entry.actions = newOrder;
    tbody.querySelectorAll<HTMLElement>('tr.action-row').forEach((tr, newIdx) => {
      tr.dataset['actionIdx'] = String(newIdx);
      tr.querySelectorAll<HTMLElement>('[data-action-idx]').forEach((el) => {
        el.dataset['actionIdx'] = String(newIdx);
      });
    });
  });
}

// ── ESM globals shim ─────────────────────────────────────────────────────
Object.assign(window, {
  renderProjectPage,
  computeStats,
  updateStats,
  buildRiskTable,
  buildRiskTableHeader,
  trendHtml,
  buildRiskRow,
  normalizeIds,
  normalizeAction,
  trigramHtml,
  renderTagsHtml,
  refreshTagWrap,
  openUserPickerModal,
  buildActionRow,
  buildActionsSubRow,
  applySort,
  sortedRisks,
  reRenderTable,
  initActionDragDrop,
});

export {};
